import {
  GridPaginationModel,
  GridRowParams,
  GridRowSelectionModel,
  GridSortItem,
  GridSortModel,
} from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDebounce } from 'src/hooks/use-debounce';
import { useTableFiltersPersistContext } from 'src/providers/table-filters-persist.provider';
import { paths } from 'src/routes/paths';
import { useEventsStore } from 'src/store/benefits/events.store';
import { useMyDataStore } from 'src/store/me/me.store';
import { EventListFilters } from 'src/types/events';
import {
  restoreEventsDrawerFilterDraft,
  restoreEventsDrawerFilterDraftFromMappedFilters,
  type EventsDrawerApplyPayload,
  type EventsDrawerFilterDraft,
} from './components/events-drawer-filters.hook';
import { getEventsColumns } from './components/events-list-columns';

export type EventRow = {
  id: string;
  eventId: string;
  image: string;
  name: string;
  startDate: string | Date;
  endDate: string | Date;
  status: string;
  statusLabel?: string;
};

type EventSortModel = GridSortItem | null;

type RequestParams = {
  searchText: string;
  filters: EventListFilters;
  drawerFilters: EventsDrawerFilterDraft | null;
  sortModel: EventSortModel;
  paginationModel: GridPaginationModel;
};

const DEFAULT_PAGINATION_MODEL: GridPaginationModel = { page: 0, pageSize: 10 };

const restorePaginationModel = (
  paginationModel: GridPaginationModel | null | undefined
): GridPaginationModel => {
  if (!paginationModel) {
    return DEFAULT_PAGINATION_MODEL;
  }

  const page = Number(paginationModel.page);
  const pageSize = Number(paginationModel.pageSize);

  return {
    page: Number.isInteger(page) && page >= 0 ? page : DEFAULT_PAGINATION_MODEL.page,
    pageSize:
      Number.isInteger(pageSize) && pageSize > 0 ? pageSize : DEFAULT_PAGINATION_MODEL.pageSize,
  };
};

const getEventImage = (image: unknown): string => {
  if (typeof image === 'string') {
    return image;
  }

  if (Array.isArray(image)) {
    const firstImage = image[0];

    if (typeof firstImage === 'string') {
      return firstImage;
    }

    if (firstImage && typeof firstImage === 'object' && 'urlName' in firstImage) {
      return String(firstImage.urlName || '');
    }
  }

  return '';
};

export default function useEventsList() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const user = useMyDataStore((state) => state.me);
  const [getEventsList, exportData, isLoadingEventsList, eventsList] = useEventsStore((store) => [
    store.getEventsList,
    store.exportData,
    store.isLoadingEventsList,
    store.eventsList,
  ]);
  const { updateFilters, getFilters } = useTableFiltersPersistContext();
  const searchParamsFilters = getFilters<RequestParams>([
    paths.dashboard.eventDetails(':id'),
    paths.dashboard.eventCreate,
  ]);

  const TABS = [
    { value: 'events' as const, label: 'Listagem', icon: 'lucide:list' },
    { value: 'reports' as const, label: 'Relatórios', icon: 'lucide:file-text' },
  ];
  const [currentTab, setCurrentTab] = useState<'events' | 'reports'>('events');
  const [searchText, setSearchText] = useState(searchParamsFilters?.searchText || '');
  const debouncedSearchText = useDebounce(searchText, 500);
  const [filters, setFilters] = useState<EventListFilters>(searchParamsFilters?.filters || []);
  const [appliedDraftFilters, setAppliedDraftFilters] = useState<EventsDrawerFilterDraft | null>(
    restoreEventsDrawerFilterDraft(searchParamsFilters?.drawerFilters) ||
      restoreEventsDrawerFilterDraftFromMappedFilters(searchParamsFilters?.filters)
  );
  const [sortModel, setSortModel] = useState<EventSortModel>(
    searchParamsFilters?.sortModel || null
  );
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(() =>
    restorePaginationModel(searchParamsFilters?.paginationModel)
  );
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [openFiltersDrawer, setOpenFiltersDrawer] = useState(false);
  const requestKey = useMemo(
    () => JSON.stringify({ search: debouncedSearchText, filters, sortModel }),
    [debouncedSearchText, filters, sortModel]
  );
  const previousRequestKeyRef = useRef(requestKey);

  useEffect(() => {
    if (previousRequestKeyRef.current !== requestKey) {
      previousRequestKeyRef.current = requestKey;

      if (paginationModel.page !== 0) {
        setPaginationModel((current) => ({ ...current, page: 0 }));
        return;
      }
    }

    updateFilters(
      JSON.stringify({
        searchText: debouncedSearchText,
        filters,
        drawerFilters: appliedDraftFilters,
        sortModel,
        paginationModel,
      })
    );

    getEventsList(debouncedSearchText, filters, sortModel, paginationModel).catch((error) => {
      enqueueSnackbar(error?.message || 'Erro ao carregar experiências.', { variant: 'error' });
    });
  }, [
    appliedDraftFilters,
    debouncedSearchText,
    enqueueSnackbar,
    filters,
    getEventsList,
    paginationModel,
    requestKey,
    sortModel,
  ]);

  const formatDate = useCallback((value?: string | Date | null) => {
    if (!value) {
      return '-';
    }

    const parsed = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(parsed.getTime())) {
      return '-';
    }

    return parsed.toLocaleDateString('pt-BR');
  }, []);

  const eventsRows = useMemo<EventRow[]>(
    () =>
      eventsList.data.map((event, index) => {
        const eventId = String(event.id || event._id || '');

        return {
          id: eventId || `${eventsList.page}-${index}-${event.name}`,
          eventId,
          image: getEventImage(event.image),
          name: event.name,
          startDate: event.startDate,
          endDate: event.endDate,
          status: event.status,
          statusLabel: event.statusLabel,
        };
      }),
    [eventsList.data, eventsList.page]
  );

  const eventsColumns = useMemo(() => getEventsColumns({ formatDate }), [formatDate]);
  const gridSortModel = useMemo<GridSortModel>(() => (sortModel ? [sortModel] : []), [sortModel]);

  const handleChangeTab = useCallback(
    (newValue: 'events' | 'reports') => setCurrentTab(newValue),
    []
  );

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    const nextSort = model[0] ?? null;
    const allowedFields = ['name', 'status', 'startDate', 'endDate'];
    setSortModel(nextSort && allowedFields.includes(nextSort.field) ? nextSort : null);
  }, []);

  const handleFilterButtonClick = useCallback(() => setOpenFiltersDrawer(true), []);
  const onCloseFilters = useCallback(() => setOpenFiltersDrawer(false), []);
  const onApplyFilters = useCallback((payload: EventsDrawerApplyPayload) => {
    setFilters(payload.mappedFilters);
    setAppliedDraftFilters(payload.draftFilters);
  }, []);

  const handleRowClick = useCallback(
    (params: GridRowParams<EventRow>) => {
      const eventId = String(params.row?.eventId || params.id || '');

      if (eventId) {
        navigate(paths.dashboard.eventDetails(eventId));
      }
    },
    [navigate]
  );

  const selectedFiltersCount = useMemo(() => {
    if (!appliedDraftFilters) {
      return 0;
    }

    return (
      (appliedDraftFilters.startDate || appliedDraftFilters.endDate ? 1 : 0) +
      (appliedDraftFilters.status ? 1 : 0)
    );
  }, [appliedDraftFilters]);

  const exportReport = useCallback(async () => {
    const ids = selectedRows.map((id) => String(id));

    if (ids.length === 0) {
      enqueueSnackbar('Por favor, selecione alguma experiência.', { variant: 'error' });
      return;
    }

    await exportData(ids, user?.name || 'Desconhecido');
    enqueueSnackbar('Seu relatório está sendo gerado!', { variant: 'info' });
    setCurrentTab('reports');
    setSelectedRows([]);
  }, [enqueueSnackbar, exportData, selectedRows, user?.name]);

  return {
    eventsList,
    eventsRows,
    eventsColumns,
    isLoadingEventsList,
    TABS,
    currentTab,
    searchText,
    gridSortModel,
    paginationModel,
    selectedRows,
    selectedFiltersCount,
    appliedDraftFilters,
    openFiltersDrawer,
    setSearchText,
    setPaginationModel,
    setSelectedRows,
    handleChangeTab,
    handleSortModelChange,
    handleFilterButtonClick,
    onCloseFilters,
    onApplyFilters,
    handleRowClick,
    navigate,
    exportReport,
  };
}
